import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCollections } from '../hooks/useCollections';
import { useRepositories } from '../hooks/useRepositories';
import CollectionCard from '../components/CollectionCard';
import ConfirmDialog from '../components/ConfirmDialog';
import GroupLinkDialog from '../components/GroupLinkDialog';
import MakePrivateDialog from '../components/MakePrivateDialog';
import UserAvatar from '../components/UserAvatar';
import { ActionError, EmptyState, LoadingState, StateForError } from '../components/PageStates';
import type { Collection } from '../domain/collections';
import { audienceActionsFor, leaveCollectionMessage } from '../utils/audienceActions';
import { groupCollections, OwnerGroup } from '../utils/collectionGroups';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { Link2, Mail, Plus, Users } from 'lucide-react';

type Pending =
  | { kind: 'leave'; collection: Collection }
  | { kind: 'makePrivate'; collection: Collection }
  | null;

/**
 * Everything I can see, grouped by how I relate to it (015 FR-024, FR-025):
 * mine, private collections I'm a member of, and open ones I follow — each
 * group with only the actions that apply to it.
 */
export default function MyCollections() {
  const collections = useCollections();
  const { audience } = useRepositories();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      collections.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const actionsFor = (c: Collection): React.ReactNode => {
    const can = audienceActionsFor(c);
    const buttons: React.ReactNode[] = [];
    if (can.makePrivate)
      buttons.push(
        <Button
          key="private"
          variant="ghost"
          size="sm"
          className="rounded-full"
          aria-label={`Make ${c.name} private`}
          onClick={() => setPending({ kind: 'makePrivate', collection: c })}
        >
          Make private
        </Button>,
      );
    if (can.leave)
      buttons.push(
        <Button
          key="leave"
          variant="ghost"
          size="sm"
          className="rounded-full"
          aria-label={`Leave ${c.name}`}
          onClick={() => setPending({ kind: 'leave', collection: c })}
        >
          Leave
        </Button>,
      );
    if (can.unmute && c.isMuted)
      buttons.push(
        <Button
          key="unmute"
          variant="secondary"
          size="sm"
          className="rounded-full"
          aria-label={`Unmute ${c.name}`}
          disabled={busy}
          onClick={() => act(() => audience.unmute(c.id))}
        >
          Unmute
        </Button>,
      );
    else if (can.mute)
      buttons.push(
        <Button
          key="mute"
          variant="secondary"
          size="sm"
          className="rounded-full"
          aria-label={`Mute ${c.name}`}
          disabled={busy}
          onClick={() => act(() => audience.mute(c.id))}
        >
          Mute
        </Button>,
      );
    return buttons.length ? buttons : undefined;
  };

  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">Collections</h1>
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" className="rounded-full" asChild>
          <Link to="/invitations">
            <Mail />
            Invitations
          </Link>
        </Button>
        <Button
          variant="secondary"
          className="rounded-full"
          onClick={() => setLinkDialogOpen(true)}
        >
          <Link2 />
          Make a group link
        </Button>
        <Button className="rounded-full" asChild>
          <Link to="/collections/create">
            <Plus />
            New collection
          </Link>
        </Button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (collections.status === 'loading') {
    body = <LoadingState label="Loading collections" />;
  } else if (collections.status === 'error') {
    body = <StateForError error={collections.error} onRetry={collections.reload} />;
  } else {
    const groups = groupCollections(collections.data);
    const grid = (list: Collection[]) => (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <CollectionCard key={c.id} collection={c} actions={actionsFor(c)} />
        ))}
      </div>
    );
    body = (
      <>
        {groups.owned.length > 0 && (
          <Group id="collections-mine" title="Mine">
            {grid(groups.owned)}
          </Group>
        )}
        {groups.member.length > 0 && (
          <Group id="collections-member" title="Member of">
            <ByOwner prefix="member" groups={groups.member} grid={grid} />
          </Group>
        )}
        {groups.following.length > 0 && (
          <Group id="collections-following" title="Following">
            <ByOwner prefix="following" groups={groups.following} grid={grid} />
          </Group>
        )}
        {groups.member.length === 0 && groups.following.length === 0 && (
          <EmptyState
            icon={<Users className="size-8" />}
            title="Nothing shared with you yet"
            description="When someone invites you, their collections show up here. Send a group link to connect with the people you trade with."
            action={
              <Button className="rounded-full" onClick={() => setLinkDialogOpen(true)}>
                Make a group link
              </Button>
            }
          />
        )}
      </>
    );
  }

  return (
    <div className="space-y-12">
      {header}
      {error !== null && <ActionError error={error} />}
      {body}
      <GroupLinkDialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen} />
      {pending?.kind === 'leave' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setPending(null)}
          title={`Leave ${pending.collection.name}?`}
          description={<p>{leaveCollectionMessage(pending.collection)}</p>}
          confirmLabel="Leave"
          onConfirm={() => act(() => audience.leave(pending.collection.id))}
        />
      )}
      {pending?.kind === 'makePrivate' && (
        <MakePrivateDialog
          collection={pending.collection}
          open
          onOpenChange={(open) => !open && setPending(null)}
          onDone={() => collections.reload()}
        />
      )}
    </div>
  );
}

function Group({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-6">
      <h2 id={id} className="text-2xl font-bold text-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ByOwner({
  prefix,
  groups,
  grid,
}: {
  prefix: string;
  groups: OwnerGroup[];
  grid: (list: Collection[]) => React.ReactNode;
}) {
  return (
    <div className="space-y-8">
      {groups.map(({ owner, collections }) => {
        const id = `${prefix}-${owner.id}`;
        return (
          <section key={owner.id} aria-labelledby={id}>
            <h3
              id={id}
              className="mb-4 flex items-center gap-3 text-lg font-semibold text-muted-foreground"
            >
              <UserAvatar person={owner} className="size-8" />
              {fullName(owner)}
            </h3>
            {grid(collections)}
          </section>
        );
      })}
    </div>
  );
}
